import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useIdleTimer } from "react-idle-timer/legacy";
import SessionTimeoutModal from "../session.timeout.modal";
import { useInterval } from "../../hooks/useInterval";
import {
  IDLE_TIMEOUT_MS,
  WARNING_DURATION_MS,
  SESSION_LOGOUT_REASON_IDLE,
} from "../../utils/session.constants";
import { sessionLogout } from "../../utils/sessionLogout";
import { msalReady } from "../../utils/msalInstance";
import { useAbsoluteSessionTimeout } from "../../hooks/useAbsoluteSessionTimeout";
import { useSessionLogoutBroadcast } from "./useSessionBroadcast";

/** Must match `events` on useIdleTimer. IdleTimer unbinds these during prompt. */
const IDLE_ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "wheel",
];

const SessionTimeoutManager = () => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(60);
  const [msalSyncComplete, setMsalSyncComplete] = useState(false);

  useEffect(() => {
    let alive = true;
    msalReady.then(() => {
      if (alive) setMsalSyncComplete(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  const account = instance.getActiveAccount() || accounts[0];
  const timerReady =
    msalSyncComplete &&
    isAuthenticated &&
    Boolean(account) &&
    inProgress === InteractionStatus.None;

  const instanceRef = useRef(instance);
  const accountRef = useRef(account);
  instanceRef.current = instance;
  accountRef.current = account;

  const handleIdle = useCallback(() => {
    setShowWarning(false);
    const acc = accountRef.current;
    const inst = instanceRef.current;
    if (acc) {
      sessionLogout(inst, acc, {
        reason: SESSION_LOGOUT_REASON_IDLE,
        broadcast: true,
      });
    }
  }, []);

  const idleTimer = useIdleTimer({
    timeout: IDLE_TIMEOUT_MS,
    promptBeforeIdle: WARNING_DURATION_MS,
    throttle: 500,
    eventsThrottle: 500,
    events: IDLE_ACTIVITY_EVENTS,
    onPrompt: () => setShowWarning(true),
    onIdle: handleIdle,
    onActive: () => setShowWarning(false),
    startOnMount: false,
    startManually: true,
    crossTab: true,
    syncTimers: 200,
    leaderElection: true,
    name: "ctpo-session-idle",
    disabled: !timerReady,
  });

  const idleTimerRef = useRef(idleTimer);
  idleTimerRef.current = idleTimer;

  useSessionLogoutBroadcast(instance, account);

  useAbsoluteSessionTimeout({
    instance,
    account,
    isAuthenticated,
    inProgress,
  });

  useEffect(() => {
    if (!timerReady) return undefined;
    idleTimerRef.current.start();
    return undefined;
  }, [timerReady, account?.localAccountId]);

  useInterval(
    () => {
      if (!showWarning) return;
      const ms = idleTimerRef.current.getRemainingTime();
      setRemainingSeconds(Math.max(0, Math.ceil(ms / 1000)));
    },
    showWarning ? 1000 : null,
  );

  useEffect(() => {
    if (!showWarning) return;
    const ms = idleTimerRef.current.getRemainingTime();
    setRemainingSeconds(Math.max(0, Math.ceil(ms / 1000)));
  }, [showWarning]);

  // During prompt, react-idle-timer does not listen for activity; bind explicitly.
  useEffect(() => {
    if (!showWarning) return undefined;

    let lastDismissAt = 0;
    const throttleMs = 500;

    const onActivityDuringPrompt = () => {
      const now = Date.now();
      if (now - lastDismissAt < throttleMs) return;
      lastDismissAt = now;
      idleTimerRef.current.activate();
      setShowWarning(false);
    };

    IDLE_ACTIVITY_EVENTS.forEach((name) => {
      window.addEventListener(name, onActivityDuringPrompt, true);
    });
    return () => {
      IDLE_ACTIVITY_EVENTS.forEach((name) => {
        window.removeEventListener(name, onActivityDuringPrompt, true);
      });
    };
  }, [showWarning]);

  if (!timerReady) {
    return null;
  }

  return (
    <SessionTimeoutModal
      open={showWarning}
      remainingSeconds={remainingSeconds}
    />
  );
};

export default SessionTimeoutManager;
